import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

export const SESSION_STALE_MS = 24 * 60 * 60 * 1000;
export const HEARTBEAT_MIN_INTERVAL_MS = 60 * 1000;
const LAST_ACTIVE_KEY = "last_active_at";
let configured = false;
let refreshPromise = null;
let heartbeatPromise = null;
let lastHeartbeatSentAt = 0;
let lastActivityWrite = 0;

export function markSessionActive(force = false) {
  const now = Date.now();
  if (!force && now - lastActivityWrite < 30 * 1000) return;
  lastActivityWrite = now;
  localStorage.setItem(LAST_ACTIVE_KEY, String(now));
}

export function getLastActiveAt() {
  const raw = localStorage.getItem(LAST_ACTIVE_KEY);
  const parsed = raw ? Number(raw) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export function persistAuthTokens(data = {}) {
  if (data.access_token) {
    localStorage.setItem("token", data.access_token);
  }
  if (data.refresh_token) {
    localStorage.setItem("refresh_token", data.refresh_token);
  }
  markSessionActive(true);
}

export function clearStoredSession() {
  [
    "token",
    "refresh_token",
    "user_id",
    "username",
    "email",
    "phone_number",
    "role",
    "providerId",
    "addresses",
    "impersonated_by_user_id",
    LAST_ACTIVE_KEY,
  ].forEach((key) => localStorage.removeItem(key));
  lastHeartbeatSentAt = 0;
}

export async function refreshSession(reason = "silent_refresh") {
  if (refreshPromise) return refreshPromise;

  const refreshToken = localStorage.getItem("refresh_token");
  const headers = refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {};

  refreshPromise = axios
    .post(`${API}/api/refresh`, { reason }, { headers, withCredentials: true, skipAuthRefresh: true })
    .then((response) => {
      persistAuthTokens(response.data || {});
      return response.data;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export async function sendSessionHeartbeat(reason = "heartbeat", inactiveMs = 0, { force = false } = {}) {
  const token = localStorage.getItem("token");
  if (!token) return null;
  if (heartbeatPromise) return heartbeatPromise;

  const now = Date.now();
  if (!force && now - lastHeartbeatSentAt < HEARTBEAT_MIN_INTERVAL_MS) return null;
  lastHeartbeatSentAt = now;

  heartbeatPromise = axios.post(
      `${API}/api/session/heartbeat`,
      {
        reason,
        inactive_ms: inactiveMs,
        visibility: document.visibilityState,
        path: window.location.hash || window.location.pathname,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      }
    )
    .catch(() => null)
    .finally(() => {
      heartbeatPromise = null;
    });

  return heartbeatPromise;
}

function notifySessionExpired(payload = {}) {
  window.dispatchEvent(new CustomEvent("fastmax:session-expired", { detail: payload }));
}

export function configureApiClient({ onSessionExpired } = {}) {
  if (configured) return;
  configured = true;

  axios.defaults.withCredentials = true;

  axios.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    config.headers = config.headers || {};
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config || {};
      const status = error.response?.status;
      const authError = error.response?.data?.error;

      const shouldTryRefresh =
        status === 401 &&
        !originalRequest._retry &&
        !originalRequest.skipAuthRefresh &&
        localStorage.getItem("refresh_token") &&
        ["token_expired", "invalid_token"].includes(authError);

      if (shouldTryRefresh) {
        originalRequest._retry = true;
        try {
          await refreshSession("retry_after_401");
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
          return axios(originalRequest);
        } catch (refreshError) {
          clearStoredSession();
          const payload = refreshError.response?.data || { error: "session_expired" };
          notifySessionExpired(payload);
          onSessionExpired?.(payload);
          return Promise.reject(refreshError);
        }
      }

      if (status === 401 && ["session_expired", "auth_required"].includes(authError)) {
        clearStoredSession();
        const payload = error.response?.data || { error: authError };
        notifySessionExpired(payload);
        onSessionExpired?.(payload);
      }

      return Promise.reject(error);
    }
  );
}
